import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, AlertCircle, Download, Building2, FlaskConical, Sparkles, Play } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: 'classifying', label: 'Classify & match companies', stages: ['queued', 'classifying', 'sub_classifying', 'matching', 'auditing', 'excluding', 'web_validating'] },
  { key: 'committing_companies', label: 'Commit companies', stages: ['committing_companies'] },
  { key: 'matching_assets', label: 'Match assets', stages: ['matching_assets'] },
  { key: 'committing_assets', label: 'Commit assets', stages: ['committing_assets'] },
  { key: 'done', label: 'Summary', stages: ['done'] },
];
const RUNNING = STEPS.flatMap(s => s.stages).filter(s => s !== 'done');

const REASON_LABEL = {
  no_email_and_no_match: 'No email & no CRM match',
  investor_no_email: 'Investor without email',
  insufficient_data: 'Insufficient data to classify',
  investor_module_deactivated: 'Investor module off',
  bucket_cro_cdmo: 'CRO/CDMO (never auto-approved)',
  bucket_service: 'Service provider (never auto-approved)',
  bucket_unclear: 'Unclear classification',
};
const reasonLabel = (r) => REASON_LABEL[r] || r.replace(/^excluded_/, 'Excluded: ').replace(/^failed_(\w+)_filter$/, 'Failed $1 rule').replace(/^bucket_/, 'Classified as ').replace(/_/g, ' ');

const Stat = ({ label, value, tone = 'text-slate-900', testid }) => (
  <div className="rounded-lg bg-white/70 border border-slate-200 px-3 py-2" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`text-xl font-bold leading-tight ${tone}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{value ?? 0}</p>
  </div>
);

export const AutoImportPanel = ({ importTag, onDone }) => {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    let timer;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/imports/${importTag}/status`, { withCredentials: true });
        setJob(prev => {
          if (prev && RUNNING.includes(prev.stage) && !RUNNING.includes(r.data.stage)) onDone?.();
          return r.data;
        });
        if (RUNNING.includes(r.data.stage)) timer = setTimeout(tick, 4000);
      } catch { /* noop */ }
    };
    tick();
    return () => clearTimeout(timer);
  }, [importTag]); // eslint-disable-line

  if (!job) return null;
  const stage = job.stage || 'ready';
  const running = RUNNING.includes(stage);
  const summary = job.summary;
  const stepIdx = STEPS.findIndex(s => s.stages.includes(stage));

  const startAutoCommit = async () => {
    setStarting(true);
    try {
      await axios.post(`${API}/imports/${importTag}/auto-commit`, {}, { withCredentials: true });
      toast.success('Auto import started — committing approved companies, then assets.');
      setJob(j => ({ ...j, stage: 'committing_companies' }));
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start auto import');
    } finally { setStarting(false); }
  };

  const downloadSummary = async () => {
    try {
      const r = await axios.get(`${API}/imports/${importTag}/summary.xlsx`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `${importTag}_import_summary.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  if (!running && !summary && stage !== 'error') {
    if (stage !== 'ready') return null;
    return (
      <div className="mb-5 flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="auto-import-offer">
        <p className="text-sm text-emerald-900"><Sparkles className="w-4 h-4 inline mr-1.5 -mt-0.5" />Classification finished. Commit approved companies and their assets in one go?</p>
        <Button size="sm" onClick={startAutoCommit} disabled={starting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="auto-import-start-btn">
          {starting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1" />} Auto import now
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="auto-import-panel">
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-4" data-testid="auto-import-stepper">
        {STEPS.map((s, i) => {
          const done = stage === 'done' ? true : i < stepIdx;
          const active = i === stepIdx && running;
          return (
            <li key={s.key} className="flex items-center gap-2" data-testid={`auto-step-${s.key}`}>
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${done ? 'bg-emerald-100 text-emerald-800' : active ? 'bg-sky-100 text-sky-800' : 'bg-slate-200 text-slate-500'}`}>
                {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : active ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span className="w-3.5 h-3.5 rounded-full border border-current inline-block" />}
                {s.label}
                {active && job.stage_total ? <span className="opacity-70">{job.stage_progress ?? 0}/{job.stage_total}</span> : null}
              </span>
              {i < STEPS.length - 1 && <span className="text-slate-300">→</span>}
            </li>
          );
        })}
      </ol>

      {stage === 'error' && (
        <p className="text-sm text-rose-700 flex items-center gap-1.5" data-testid="auto-import-error"><AlertCircle className="w-4 h-4" /> {job.error}</p>
      )}

      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="auto-import-summary">
          <div>
            <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-2"><Building2 className="w-3.5 h-3.5 text-sky-600" /> Companies · {summary.companies.parsed} in sheet</p>
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Newly added" value={summary.companies.new} tone="text-emerald-700" testid="summary-companies-new" />
              <Stat label="Enriched" value={summary.companies.enriched} tone="text-sky-700" testid="summary-companies-enriched" />
              <Stat label="Pending review" value={summary.companies.pending_review} tone="text-amber-700" testid="summary-companies-pending" />
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              New by type: {Object.entries(summary.companies.new_by_bucket || {}).filter(([, v]) => v).map(([k, v]) => `${v} ${k}`).join(' · ') || 'none'}
            </p>
            {Object.keys(summary.companies.pending_by_reason || {}).length > 0 && (
              <p className="text-[11px] text-slate-500 mt-1">
                Left for review: {Object.entries(summary.companies.pending_by_reason).slice(0, 5).map(([k, v]) => `${v} ${reasonLabel(k)}`).join(' · ')}
              </p>
            )}
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-2"><FlaskConical className="w-3.5 h-3.5 text-fuchsia-600" /> Assets · {summary.assets.parsed} in sheet</p>
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Newly added" value={summary.assets.new} tone="text-emerald-700" testid="summary-assets-new" />
              <Stat label="Enriched" value={summary.assets.enriched} tone="text-sky-700" testid="summary-assets-enriched" />
              <Stat label="Unmatched" value={summary.assets.unmatched} tone="text-amber-700" testid="summary-assets-unmatched" />
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              {summary.assets.unchanged} already up to date · {summary.assets.pharma_promoted_to_biotech} pharma→biotech promoted · {summary.assets.orphan_assets_relinked} orphan assets relinked
            </p>
          </div>
          <div className="lg:col-span-2 flex flex-wrap items-center gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={downloadSummary} data-testid="download-summary-btn"><Download className="w-3.5 h-3.5 mr-1" /> Summary.xlsx</Button>
            <Link to={`/asset-imports/${importTag}`}><Button size="sm" variant="outline" data-testid="summary-open-assets-btn"><FlaskConical className="w-3.5 h-3.5 mr-1" /> Asset rows</Button></Link>
            <Link to={`/biotech?cohort=${importTag}`}><Button size="sm" variant="outline" data-testid="summary-open-biotechs-btn"><Building2 className="w-3.5 h-3.5 mr-1" /> Biotechs from this sheet</Button></Link>
            <Button size="sm" variant="ghost" onClick={startAutoCommit} disabled={starting} className="text-slate-500" data-testid="auto-import-rerun-btn">
              <Play className="w-3.5 h-3.5 mr-1" /> Re-run on newly approved rows
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
